import {
  DocumentAssemblerConditionalBranchKind,
  DocumentAssemblerTemplateVersionStatus,
} from './types';

export const DocumentAssemblerGenerationStatus = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;
export type DocumentAssemblerGenerationStatus =
  (typeof DocumentAssemblerGenerationStatus)[keyof typeof DocumentAssemblerGenerationStatus];

// Three-state on purpose: a boolean would force "undecided" to masquerade as one of the other two.
export const DocumentAssemblerGenerationValueDisposition = {
  IN_DOCUMENT: 'IN_DOCUMENT',
  REMOVED_BY_BRANCH_DECISION: 'REMOVED_BY_BRANCH_DECISION',
  UNDECIDED: 'UNDECIDED',
} as const;
export type DocumentAssemblerGenerationValueDisposition =
  (typeof DocumentAssemblerGenerationValueDisposition)[keyof typeof DocumentAssemblerGenerationValueDisposition];

export interface IDocumentAssemblerPlaceholdersSurroundingContext {
  paragraphKey: string;
  prefix: string;
  tag: string;
  suffix: string;
}

export interface IDocumentAssemblerGenerationPlaceholder {
  id: string;
  ordinal: number;
  /** What the template asked for. */
  instruction: string;
  /** What the caller wrote. null with disposition IN_DOCUMENT means still needed, not empty. */
  value: string | null;
  writtenAt: string | null;
  disposition: DocumentAssemblerGenerationValueDisposition;
  placeholdersSurroundingContext: IDocumentAssemblerPlaceholdersSurroundingContext;
}

interface IDocumentAssemblerGenerationDecisionBase {
  conditionalTreeId: string;
  /** -1 when the tree is no longer on the version. */
  conditionalTreeOrdinal: number;
  decidedAt: string;
}

/**
 * Discriminated on `branchId`, which decides the other three fields: no winning branch means
 * there is nothing to describe, so ordinal, kind and condition are all null together rather
 * than each being independently nullable. They stay declared on both members so a caller can
 * read `condition` without narrowing first.
 */
export type IDocumentAssemblerGenerationDecision =
  | (IDocumentAssemblerGenerationDecisionBase & {
      branchId: string;
      /** null, along with kind and condition, once the branch is off the version. */
      branchOrdinal: number | null;
      kind: DocumentAssemblerConditionalBranchKind | null;
      condition: string | null;
    })
  | (IDocumentAssemblerGenerationDecisionBase & {
      /** No branch wins, so the whole passage is absent. */
      branchId: null;
      branchOrdinal: null;
      kind: null;
      condition: null;
    });

export interface IDocumentAssemblerUnresolvedPlaceholder {
  id: string;
  ordinal: number;
  instruction: string;
  conditionalTreeId: string | null;
  conditionalBranchId: string | null;
}

export interface IDocumentAssemblerGenerationConditionalBranch {
  id: string;
  ordinal: number;
  kind: DocumentAssemblerConditionalBranchKind;
  condition: string | null;
}

export interface IDocumentAssemblerGenerationConditionalTree {
  id: string;
  ordinal: number;
  requiresWinningBranch: boolean;
  branches: IDocumentAssemblerGenerationConditionalBranch[];
}

export interface IDocumentAssemblerUnresolvedPlaceholdersAndTrees {
  placeholders: IDocumentAssemblerUnresolvedPlaceholder[];
  conditionalTrees: IDocumentAssemblerGenerationConditionalTree[];
  count: number;
  /** Nothing outstanding at all, so a document rendered now carries no pending marker. Nothing gates on it:
   *  a generation renders whatever it holds, and what it does not hold is marked in the page. */
  isReadyToFinalise: boolean;
}

/** What a render returns, for the finished document and for one generated from a generation alike. */
export interface IDocumentAssemblerRenderedGeneration {
  id: string;
  generatedFileId: number;
  fileName: string;
  /** The placeholders that reached the page as markers, so the caller can say how many gaps to expect. */
  pendingPlaceholders: IDocumentAssemblerUnresolvedPlaceholder[];
}

export interface IDocumentAssemblerGenerationPreviewResponse {
  preview: IDocumentAssemblerRenderedGeneration;
}

export interface IDocumentAssemblerGeneration {
  id: string;
  status: DocumentAssemblerGenerationStatus;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  companyId: number;
  companyName: string | null;
  corporationId: number | null;
  corporationName: string | null;
  userId: string | null;
  agentRun: {
    id: string;
    agentType: string;
    profileDescription: string | null;
    conversationId: string | null;
    workflowExecutionId: string | null;
  } | null;
  template: { id: string; key: string; title: string };
  templateVersion: {
    id: string;
    version: number;
    status: DocumentAssemblerTemplateVersionStatus;
  };
  authorGuidance: string | null;
  generatedFileId: number | null;
  placeholders: IDocumentAssemblerGenerationPlaceholder[];
  decisions: IDocumentAssemblerGenerationDecision[];
  unresolvedPlaceholdersAndTrees: IDocumentAssemblerUnresolvedPlaceholdersAndTrees;
}

export interface IDocumentAssemblerGenerationSummary {
  id: string;
  status: DocumentAssemblerGenerationStatus;
  createdAt: string;
  completedAt: string | null;
  companyId: number;
  companyName: string | null;
  corporationId: number | null;
  corporationName: string | null;
  generatedFileId: number | null;
  template: { id: string; key: string; title: string };
  templateVersion: { id: string; version: number };
  agentRun: {
    id: string;
    agentType: string;
    profileDescription: string | null;
  } | null;
}

export interface IDocumentAssemblerGenerationListResponse {
  generations: IDocumentAssemblerGenerationSummary[];
  count: number;
}

export interface IDocumentAssemblerGenerationResponse {
  generation: IDocumentAssemblerGeneration;
}

export type TDocumentAssemblerListGenerationsParams = {
  templateId?: string;
  templateVersionId?: string;
  companyId?: number;
  status?: DocumentAssemblerGenerationStatus;
  limit?: number;
  offset?: number;
};
